/**
 * Password policy — mirrors pms-backend/src/utils/passwordPolicy.js so the
 * checklist ticks match what the server will accept. The server stays the
 * authority (it also checks reuse of the last passwords, which the client
 * cannot know).
 */
export const PASSWORD_MIN_LENGTH = 8
export const PASSWORD_MAX_LENGTH = 128

const BLOCKED_WORDS = new Set([
  'password', 'passwrd', 'pssword', 'passw', 'pass', 'admin', 'administrator',
  'welcome', 'qwerty', 'qwertyuiop', 'asdf', 'asdfgh', 'zxcvbn', 'letmein',
  'iloveyou', 'abc', 'abcd', 'abcdef', 'abcdefg', 'abcdefgh', 'login', 'user',
  'test', 'guest', 'secret', 'changeme', 'default', 'master', 'monkey',
  'dragon', 'football', 'cricket', 'pakistan', 'karachi', 'lahore',
  'islamabad', 'superman', 'sunshine', 'princess', 'whatever', 'trustno',
  'multipack', 'multipackpk', 'mpplastic', 'mpplastics', 'plastic', 'plastics',
  'pmms', 'pms',
])

export interface PasswordPerson {
  firstName?: string | null
  lastName?: string | null
  email?: string | null
}

export interface PasswordRuleResult {
  id: string
  label: string
  /** Chip text in the checklist. */
  short: string
  met: boolean
}

function containsPersonal(value: string, person?: PasswordPerson): boolean {
  if (!person) return false
  const lower = value.toLowerCase()
  return [person.firstName, person.lastName, (person.email ?? '').split('@')[0]]
    .map((part) => (part ?? '').trim().toLowerCase())
    .filter((part) => part.length >= 3)
    .some((part) => lower.includes(part))
}

/** Every rule with its current state — drives the live checklist. */
export function evaluatePassword(value: string, person?: PasswordPerson): PasswordRuleResult[] {
  const letters = value.toLowerCase().replace(/[^a-z]/g, '')
  return [
    {
      id: 'length',
      label: `${PASSWORD_MIN_LENGTH}+ characters`,
      short: `${PASSWORD_MIN_LENGTH}+ characters`,
      met: value.length >= PASSWORD_MIN_LENGTH && value.length <= PASSWORD_MAX_LENGTH,
    },
    { id: 'upper', label: 'One uppercase letter (A–Z)', short: 'Uppercase', met: /[A-Z]/.test(value) },
    { id: 'lower', label: 'One lowercase letter (a–z)', short: 'Lowercase', met: /[a-z]/.test(value) },
    { id: 'special', label: 'One special character (@ # $ ! % …)', short: 'Symbol (@#$…)', met: /[^A-Za-z0-9]/.test(value) },
    {
      id: 'common',
      label: 'Not a common password',
      short: 'Not common',
      met: value.length > 0 && !(letters && BLOCKED_WORDS.has(letters)),
    },
    {
      id: 'personal',
      label: 'Does not contain your name or email',
      short: 'No name or email',
      met: value.length > 0 && !containsPersonal(value, person),
    },
  ]
}

/** First unmet rule as a sentence, or null when the password is acceptable. */
export function passwordPolicyError(value: string, person?: PasswordPerson): string | null {
  const failed = evaluatePassword(value, person).find((rule) => !rule.met)
  return failed ? `Password needs: ${failed.label.toLowerCase()}` : null
}

/** Strong random password that always satisfies the policy (admin "Generate"). */
export function generatePassword(length = 14): string {
  const upper = 'ABCDEFGHJKLMNPQRSTUVWXYZ'
  const lower = 'abcdefghijkmnopqrstuvwxyz'
  const digits = '23456789'
  const special = '@#$%&*!?'
  const all = upper + lower + digits + special
  const pick = (set: string) => {
    const buf = new Uint32Array(1)
    crypto.getRandomValues(buf)
    return set[buf[0]! % set.length]
  }
  const chars = [pick(upper), pick(lower), pick(digits), pick(special)]
  while (chars.length < length) chars.push(pick(all))
  // Fisher–Yates so the guaranteed classes are not always first.
  for (let i = chars.length - 1; i > 0; i -= 1) {
    const buf = new Uint32Array(1)
    crypto.getRandomValues(buf)
    const j = buf[0]! % (i + 1)
    ;[chars[i], chars[j]] = [chars[j]!, chars[i]!]
  }
  return chars.join('')
}

export type PasswordStrength = { score: 0 | 1 | 2 | 3 | 4; label: string }

/** 0 = empty · 1 weak · 2 fair · 3 good (meets policy) · 4 strong (policy + 12 chars). */
export function passwordStrength(value: string, person?: PasswordPerson): PasswordStrength {
  if (!value) return { score: 0, label: '' }
  const met = evaluatePassword(value, person).filter((rule) => rule.met).length
  const total = evaluatePassword(value, person).length
  if (met === total && value.length >= 12) return { score: 4, label: 'Strong' }
  if (met === total) return { score: 3, label: 'Good' }
  if (met >= total - 2) return { score: 2, label: 'Fair' }
  return { score: 1, label: 'Weak' }
}
